import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Github, Check, AlertCircle, FolderOpen, GitBranch } from 'lucide-react';
import { API } from '../App';
import RepoSelector from './RepoSelector';

export default function SaveToGitHub({ open, onClose, fray, frayId }) {
  const [connected, setConnected] = useState(false);
  const [checking, setChecking] = useState(true);
  const [showRepoSelector, setShowRepoSelector] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [branch, setBranch] = useState('main');
  const [path, setPath] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      setError('');
      setSuccess(false);
      API.get('/api/github/status')
        .then(r => {
          setConnected(r.data.connected);
          if (r.data.default_repo) {
            setSelectedRepo({ full_name: r.data.default_repo });
          }
        })
        .catch(() => setConnected(false))
        .finally(() => setChecking(false));

      // Generate default path
      const slug = fray?.template_slug || 'custom';
      const date = new Date().toISOString().slice(0, 10);
      setPath(`decisions/${date}-${slug}.md`);
    }
  }, [open, fray]);

  const handleSave = async () => {
    if (!selectedRepo) { setError('Select a repository'); return; }
    if (!path.trim()) { setError('Enter a file path'); return; }

    setSaving(true);
    setError('');
    try {
      const markdown = await API.get(`/api/frays/${frayId}/export/markdown`);
      await API.post('/api/github/commit', {
        repo: selectedRepo.full_name,
        branch,
        path: path.trim(),
        content: markdown.data.markdown,
        message: `Fray verdict: ${fray?.template_name || fray?.scenario?.slice(0, 60) || 'Custom Fray'}`,
      });
      setSuccess(true);
      setTimeout(() => { onClose(); }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to commit to GitHub');
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="w-full max-w-md bg-[#1A1A1A] border border-[#2A2A2A]"
          onClick={e => e.stopPropagation()}
          data-testid="save-to-github-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-[#2A2A2A]">
            <div className="flex items-center gap-2">
              <Github className="w-4 h-4 text-[#F5F5F5]" />
              <h2 className="text-sm font-semibold font-['Barlow_Condensed']">Save to GitHub</h2>
            </div>
            <button onClick={onClose} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#F5F5F5]">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {checking ? (
              <div className="flex justify-center py-6">
                <div className="w-5 h-5 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : !connected ? (
              <div className="text-center py-6">
                <Github className="w-8 h-8 text-[#A0A0A0] mx-auto mb-2" />
                <p className="text-sm text-[#A0A0A0] mb-2">GitHub not connected</p>
                <a href="/settings" className="text-xs text-[#FF4D00] hover:underline">Connect in Settings</a>
              </div>
            ) : success ? (
              <div className="text-center py-6">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#22C55E]/10 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#22C55E]" />
                </div>
                <p className="text-sm text-[#22C55E] font-medium">Committed to GitHub</p>
                <p className="text-[10px] text-[#A0A0A0] mt-1 font-mono">{selectedRepo?.full_name}/{path}</p>
              </div>
            ) : (
              <>
                {/* Repo selection */}
                <div>
                  <label className="text-xs text-[#A0A0A0] font-mono mb-1.5 block">REPOSITORY</label>
                  <button
                    onClick={() => setShowRepoSelector(true)}
                    className="w-full flex items-center gap-2 px-3 py-2.5 min-h-[44px] bg-[#0F0F0F] border border-[#2A2A2A] text-left hover:border-[#FF4D00]/40 transition-colors"
                    data-testid="select-repo-btn"
                  >
                    <FolderOpen className="w-4 h-4 text-[#FF4D00] shrink-0" />
                    <span className="text-sm truncate">
                      {selectedRepo?.full_name || 'Select a repository...'}
                    </span>
                  </button>
                </div>

                {/* Branch */}
                <div>
                  <label className="text-xs text-[#A0A0A0] font-mono mb-1.5 block">BRANCH</label>
                  <div className="relative">
                    <GitBranch className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={branch}
                      onChange={e => setBranch(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00]"
                      placeholder="main"
                      data-testid="branch-input"
                    />
                  </div>
                </div>

                {/* File path */}
                <div>
                  <label className="text-xs text-[#A0A0A0] font-mono mb-1.5 block">FILE PATH</label>
                  <input
                    type="text"
                    value={path}
                    onChange={e => setPath(e.target.value)}
                    className="w-full px-3 py-2.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm text-[#F5F5F5] focus:outline-none focus:border-[#FF4D00] font-mono"
                    placeholder="decisions/2026-03-24-review.md"
                    data-testid="file-path-input"
                  />
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-2 bg-[#EF4444]/10 border border-[#EF4444]/30">
                    <AlertCircle className="w-3.5 h-3.5 text-[#EF4444] shrink-0" />
                    <p className="text-xs text-[#EF4444]">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleSave}
                  disabled={saving || !selectedRepo || !path.trim()}
                  className="w-full px-4 py-3 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium disabled:opacity-40 flex items-center justify-center gap-2"
                  data-testid="commit-btn"
                >
                  {saving ? (
                    <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Committing...</>
                  ) : (
                    <><Github className="w-4 h-4" /> Commit to GitHub</>
                  )}
                </button>
              </>
            )}
          </div>

          <RepoSelector
            open={showRepoSelector}
            onClose={() => setShowRepoSelector(false)}
            onSelectRepo={(repo) => { setSelectedRepo(repo); setShowRepoSelector(false); }}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
